import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSignature,
  Package,
  Award,
  Briefcase,
  UserPlus,
  ClipboardCheck,
  BoxIcon,
  FileText,
  AlertTriangle,
  Users,
  Activity,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminActivity } from '@/hooks/use-admin';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { ActivityType } from '@/lib/types';

// ─── Icon Map ────────────────────────────────────────────────────────────────

const activityIcons: Record<ActivityType, React.ReactNode> = {
  jsa_signature: <FileSignature className="h-4 w-4 text-indigo-600" />,
  jsa: <FileText className="h-4 w-4 text-indigo-500" />,
  deployment: <Package className="h-4 w-4 text-emerald-600" />,
  asset: <BoxIcon className="h-4 w-4 text-blue-600" />,
  cert_update: <Award className="h-4 w-4 text-amber-600" />,
  job_status: <Briefcase className="h-4 w-4 text-purple-600" />,
  employee: <UserPlus className="h-4 w-4 text-teal-600" />,
  inspection: <ClipboardCheck className="h-4 w-4 text-cyan-600" />,
  kit: <BoxIcon className="h-4 w-4 text-orange-600" />,
  safe_work_plan: <FileText className="h-4 w-4 text-green-600" />,
  near_miss: <AlertTriangle className="h-4 w-4 text-red-600" />,
  toolbox_talk: <Users className="h-4 w-4 text-sky-600" />,
  other: <Activity className="h-4 w-4 text-gray-500" />,
};

const activityBgColors: Record<ActivityType, string> = {
  jsa_signature: 'bg-indigo-50',
  jsa: 'bg-indigo-50',
  deployment: 'bg-emerald-50',
  asset: 'bg-blue-50',
  cert_update: 'bg-amber-50',
  job_status: 'bg-purple-50',
  employee: 'bg-teal-50',
  inspection: 'bg-cyan-50',
  kit: 'bg-orange-50',
  safe_work_plan: 'bg-green-50',
  near_miss: 'bg-red-50',
  toolbox_talk: 'bg-sky-50',
  other: 'bg-gray-50',
};

// ─── Relative Time ───────────────────────────────────────────────────────────

function relativeTime(timestamp: string, timeZone: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateTimeInTimeZone(timestamp, timeZone, { dateStyle: 'medium' });
}

// ─── Skeleton Loader ─────────────────────────────────────────────────────────

function ActivitySkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 animate-pulse">
          <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-100 rounded w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function ActivityEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
        <Activity className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="text-sm font-medium text-gray-900 mb-1">No activity yet</h3>
      <p className="text-sm text-gray-500 max-w-sm">
        Activity will appear here as your team uses H3ight. Actions like signing
        JSAs, deploying assets, and updating certifications will show up in this feed.
      </p>
    </div>
  );
}

// ─── Activity Tab ────────────────────────────────────────────────────────────

export function ActivityTab() {
  const orgTimeZone = useOrgTimeZone();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useAdminActivity({ page });

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load activity feed');
    }
  }, [isError]);

  const handleEntryClick = (link: string | null) => {
    if (!link) return;
    // Preserve backend activity_type keys (e.g. job_status) but normalize legacy /jobs links.
    let normalized = link;
    if (normalized === '/jobs') {
      normalized = '/projects/list';
    } else if (normalized.startsWith('/jobs/')) {
      normalized = normalized.replace(/^\/jobs\//, '/projects/');
    }
    navigate(normalized);
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Recent activity across your organization.
        </p>
        {data && (
          <span className="text-xs text-gray-400">
            Auto-refreshes every 30s
          </span>
        )}
      </div>

      {/* Loading state */}
      {isLoading ? (
        <ActivitySkeleton />
      ) : !data?.results?.length ? (
        <ActivityEmptyState />
      ) : (
        <>
          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />

            <div className="space-y-1">
              {data.results.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => handleEntryClick(entry.link)}
                  disabled={!entry.link}
                  className="relative flex items-start gap-3 w-full text-left px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors disabled:cursor-default group"
                >
                  {/* Icon */}
                  <div
                    className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                      activityBgColors[entry.type] || 'bg-gray-50'
                    }`}
                  >
                    {activityIcons[entry.type] || activityIcons.other}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 leading-snug">
                      {entry.message}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {entry.actor && (
                        <span className="text-xs text-gray-500">
                          {entry.actor}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {relativeTime(entry.timestamp, orgTimeZone)}
                      </span>
                    </div>
                  </div>

                  {/* Link indicator */}
                  {entry.link && (
                    <span className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity self-center">
                      View &rarr;
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Pagination */}
          {(data.next || data.previous) && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-gray-600">
                {data.count} total activit{data.count !== 1 ? 'ies' : 'y'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!data.previous}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.next}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Loading indicator for refetch */}
      {!isLoading && data && isFetching && (
        <div className="flex items-center justify-center">
          <Loader2 className="h-3 w-3 text-gray-300 animate-spin transition-opacity" />
        </div>
      )}
    </div>
  );
}
